/**
 * Нарезка широких HTML-таблиц в клоне DOM перед html2pdf: несколько копий таблицы
 * с непересекающимися наборами колонок (как «горизонтальные страницы»).
 *
 * Границы горизонтальных кусков — по сумме замеренных ширин колонок (см. computeChunks).
 * Перед нарезкой логический colspan TableAggregated сводится в DOM colSpan; в куске
 * ячейка обрезается (colspan=4 + colspan=3), текст якоря дублируется в продолжении.
 *
 * Отключение для отдельных таблиц: на `<table>` или предке задать атрибут
 * `data-pdf-no-wide-table-split` (см. `PDF_EXPORT_NO_WIDE_TABLE_SPLIT_ATTR`).
 *
 * Повтор ведущих колонок в следующих горизонтальных кусках PDF: атрибут на таблице
 * или предке `data-pdf-repeat-leading-cols="N"` (см. `PDF_EXPORT_REPEAT_LEADING_COLS_ATTR`).
 */

export const PDF_EXPORT_WIDE_TABLE_LAYOUT_WIDTH_PX = 1440;

export const PDF_EXPORT_WIDE_TABLE_DEFAULT_MAX_WIDTH_PX = Math.floor((297 * 96) / 25.4) - 24;

/** Порог ширины горизонтального куска в тех же CSS px, что и замер таблицы (как `windowWidth` / host). */
export const getPdfWideTableChunkMaxWidthPx = (layoutWidthPx: number, reservePx = 24): number =>
  Math.max(400, Math.floor(layoutWidthPx) - reservePx);

/**
 * Бюджет куска, не шире печатной области A3 portrait (html2pdf `format: 'a3'`).
 * Для простых таблиц с фиксированными колонками — иначе правый край обрезается на странице.
 * Сложные агрегированные таблицы (colspan, repeat-leading-cols) обычно оставляют {@link getPdfWideTableChunkMaxWidthPx}.
 */
export const getPdfWideTableChunkMaxWidthForPrintPx = (
  layoutWidthPx: number = PDF_EXPORT_WIDE_TABLE_LAYOUT_WIDTH_PX,
): number => Math.min(getPdfWideTableChunkMaxWidthPx(layoutWidthPx), PDF_EXPORT_WIDE_TABLE_DEFAULT_MAX_WIDTH_PX);

/** Таблица с этим атрибутом на себе или на предке не нарезается по колонкам в PDF. */
export const PDF_EXPORT_NO_WIDE_TABLE_SPLIT_ATTR = 'data-pdf-no-wide-table-split';

/**
 * Сколько первых колонок сетки таблицы дублировать слева в каждом горизонтальном продолжении (куске после
 * разреза), если они не входят в срез этого куска. Задаётся на `<table>` или предке (`TableAggregated` пробрасывает attrs).
 */
export const PDF_EXPORT_REPEAT_LEADING_COLS_ATTR = 'data-pdf-repeat-leading-cols';

/** Один id на все горизонтальные куски одной исходной таблицы (для chunked PDF в fileExport). */
export const PDF_EXPORT_SLICE_GROUP_ATTR = 'data-pdf-export-slice-group';

/** Запас CSS при наборе строк в полосу (html2canvas чуть выше оценки → не берём «пограничную» tr). */
export const CHUNKED_PDF_ROW_BAND_SLACK_CSS = 28;

/** Запас mm перед переносом строки / полосы на следующую страницу PDF. */
export const CHUNKED_PDF_ROW_PAGE_SLACK_MM = 9;

/** Запас mm: новая страница до canvas, если полоса почти не влезает в остаток. */
export const CHUNKED_PDF_BAND_EST_SLACK_MM = 7;

/**
 * Доп. запас только на 1-й странице PDF (над таблицей уже есть заголовок/текст).
 * Уменьшает бюджет полосы → пограничные tr уходят на 2-ю страницу целиком.
 */
export const CHUNKED_PDF_FIRST_PAGE_EXTRA_SLACK_MM = 22;

export type TChunkedPdfBandCapture = { captureStartCss: number; captureHeightCss: number };

/** Координаты tr относительно корня экспорта (не смешивать куски wide-split). */
export const findTbodyMetricsScopeRootForChunkedPdf = (exportRoot: HTMLElement, yOffsetCss: number): HTMLElement => {
  const rootTop = exportRoot.getBoundingClientRect().top;
  const y = rootTop + yOffsetCss;

  for (const wrapper of exportRoot.querySelectorAll<HTMLElement>('.pdf-wide-table-slice-chunk')) {
    const body = wrapper.querySelector<HTMLElement>('.pdf-wide-table-slice-body-table');
    if (!body) {
      continue;
    }
    const rect = body.getBoundingClientRect();
    if (y >= rect.top - 1 && y < rect.bottom + 0.5) {
      return body;
    }
  }

  for (const wrapper of exportRoot.querySelectorAll<HTMLElement>('.pdf-wide-table-slice-chunk')) {
    const rect = wrapper.getBoundingClientRect();
    if (y >= rect.top - 1 && y < rect.bottom + 0.5) {
      return wrapper;
    }
  }

  return exportRoot;
};

export const collectTbodyRowBottomsForChunkedPdf = (scopeRoot: HTMLElement, coordinateRoot: HTMLElement): number[] => {
  const originTop = coordinateRoot.getBoundingClientRect().top;
  const values = Array.from(scopeRoot.querySelectorAll('tbody tr'))
    .map((tr) => Math.round(tr.getBoundingClientRect().bottom - originTop))
    .filter((v) => v > 0)
    .sort((a, b) => a - b);

  const uniq: number[] = [];
  for (const v of values) {
    if (!uniq.length || v > uniq[uniq.length - 1]) {
      uniq.push(v);
    }
  }
  return uniq;
};

export const pickRowAwareBandHeightCssForChunkedPdf = (
  yOffsetCss: number,
  maxCssThisPage: number,
  rowBottomsCss: number[],
): number | null => {
  if (rowBottomsCss.length === 0) {
    return Math.max(1, maxCssThisPage);
  }

  const endLimit = yOffsetCss + Math.max(1, maxCssThisPage);
  let bestEnd = -1;
  for (const b of rowBottomsCss) {
    if (b > yOffsetCss && b <= endLimit) {
      bestEnd = b;
    }
    if (b > endLimit) {
      break;
    }
  }

  if (bestEnd > yOffsetCss) {
    return Math.max(1, bestEnd - yOffsetCss);
  }

  for (const b of rowBottomsCss) {
    if (b > yOffsetCss) {
      return Math.max(1, b - yOffsetCss);
    }
  }

  return null;
};

/** yOffset следующего блока ниже текущей позиции (следующий wide-chunk, tr и т.д.). */
export const findChunkedContentYOffsetBelow = (
  exportRoot: HTMLElement,
  yOffsetCss: number,
  totalHeightCssPx: number,
): number => {
  const rootTop = exportRoot.getBoundingClientRect().top;
  const yViewport = rootTop + yOffsetCss;
  let nextTopCss = totalHeightCssPx;

  const markTop = (viewportTop: number) => {
    if (viewportTop > yViewport + 0.5) {
      nextTopCss = Math.min(nextTopCss, Math.round(viewportTop - rootTop));
    }
  };

  for (const wrapper of exportRoot.querySelectorAll<HTMLElement>('.pdf-wide-table-slice-chunk')) {
    markTop(wrapper.getBoundingClientRect().top);
  }

  for (const tr of exportRoot.querySelectorAll('tbody tr')) {
    markTop(tr.getBoundingClientRect().top);
  }

  return nextTopCss;
};

export const adjustBandCaptureForWideTableSlice = (
  exportRoot: HTMLElement,
  yOffsetCss: number,
  bandHeightCss: number,
): TChunkedPdfBandCapture => {
  const rootTop = exportRoot.getBoundingClientRect().top;
  const bandTopViewport = rootTop + yOffsetCss;

  for (const wrapper of exportRoot.querySelectorAll<HTMLElement>('.pdf-wide-table-slice-chunk')) {
    const bodyTable = wrapper.querySelector<HTMLElement>('.pdf-wide-table-slice-body-table');
    if (!bodyTable) {
      continue;
    }

    const bodyRect = bodyTable.getBoundingClientRect();
    if (bandTopViewport < bodyRect.top - 1 || bandTopViewport >= bodyRect.bottom) {
      continue;
    }

    const firstBodyRow = bodyTable.querySelector('tbody tr');
    const firstRowTop = firstBodyRow?.getBoundingClientRect().top ?? bodyRect.top;
    if (bandTopViewport > firstRowTop + 2) {
      break;
    }

    const wrapperTopCss = Math.floor(wrapper.getBoundingClientRect().top - rootTop);
    const captureStartCss = Math.min(yOffsetCss, wrapperTopCss);
    const bandEndCss = yOffsetCss + bandHeightCss;
    return {
      captureStartCss,
      captureHeightCss: Math.max(1, bandEndCss - captureStartCss),
    };
  }

  return { captureStartCss: yOffsetCss, captureHeightCss: bandHeightCss };
};

/** Небольшой запас сверху в снимке — html2canvas иногда съедает верхнюю границу tr. */
const CHUNKED_PDF_CAPTURE_TOP_BLEED_CSS = 2;

/**
 * Снимок полосы: от верха первой tr в диапазоне (не от «хвоста» предыдущей строки по bottom),
 * высота до bandEnd = yOffset + rowBandHeight.
 */
export const resolveChunkedBandCaptureForChunkedPdf = (
  exportRoot: HTMLElement,
  metricsScopeRoot: HTMLElement,
  yOffsetCss: number,
  rowBandHeightCss: number,
  captureFromAdjust: TChunkedPdfBandCapture,
): TChunkedPdfBandCapture => {
  const rootTop = exportRoot.getBoundingClientRect().top;
  const bandEndCss = yOffsetCss + rowBandHeightCss;
  let firstRowTopCss: number | null = null;

  for (const tr of metricsScopeRoot.querySelectorAll('tbody tr')) {
    const rect = tr.getBoundingClientRect();
    const top = Math.round(rect.top - rootTop);
    const bottom = Math.round(rect.bottom - rootTop);
    if (bottom > yOffsetCss + 0.5 && top < bandEndCss + 0.5) {
      firstRowTopCss = firstRowTopCss === null ? top : Math.min(firstRowTopCss, top);
    }
  }

  let captureStartCss = captureFromAdjust.captureStartCss;
  if (firstRowTopCss !== null) {
    captureStartCss = Math.min(captureStartCss, firstRowTopCss);
  }
  captureStartCss = Math.max(0, captureStartCss - CHUNKED_PDF_CAPTURE_TOP_BLEED_CSS);

  return {
    captureStartCss,
    captureHeightCss: Math.max(1, bandEndCss - captureStartCss),
  };
};

/** Следующая полоса — с top первой tr ниже bandEnd, а не с bottom предыдущей (избегает обрезки сверху). */
export const findNextChunkedYOffsetAfterBand = (
  metricsScopeRoot: HTMLElement,
  exportRoot: HTMLElement,
  bandEndCss: number,
): number => {
  const rootTop = exportRoot.getBoundingClientRect().top;
  let nextTopCss = bandEndCss;

  for (const tr of metricsScopeRoot.querySelectorAll('tbody tr')) {
    const top = Math.round(tr.getBoundingClientRect().top - rootTop);
    const bottom = Math.round(tr.getBoundingClientRect().bottom - rootTop);
    if (bottom > bandEndCss + 0.5) {
      nextTopCss = Math.min(nextTopCss, top);
    }
  }

  return nextTopCss;
};

export const getWideTableSliceHeadExtraCss = (exportRoot: HTMLElement, yOffsetCss: number): number => {
  const preview = adjustBandCaptureForWideTableSlice(exportRoot, yOffsetCss, 1);
  return Math.max(0, yOffsetCss - preview.captureStartCss);
};

export const cssHeightToMmForChunkedPdf = (heightCss: number, pageHeightCssPx: number, innerHeightMm: number): number =>
  (heightCss / pageHeightCssPx) * innerHeightMm;

export const mmToPageCssPx = (mm: number, pageHeightCssPx: number, innerHeightMm: number): number =>
  Math.round((mm / Math.max(1, innerHeightMm)) * pageHeightCssPx);

type TPlaceChunkedBandParams = {
  pdf: import('jspdf').jsPDF;
  canvas: HTMLCanvasElement;
  bandHeightMm: number;
  imageType: string;
  imageQuality: number;
  marginLeft: number;
  marginTop: number;
  innerWidthMm: number;
  innerHeightMm: number;
  startYmm: number;
};

/** Одна полоса canvas на PDF; не влезает — новая страница, без нарезки по px (режет строки). */
export const placeChunkedBandOnPdf = (p: TPlaceChunkedBandParams): number => {
  let yMm = p.startYmm;
  const remainingMm = p.innerHeightMm - yMm;
  // Не листаем повторно, если уже стоим в начале новой страницы (y=0):
  // иначе возможен «пустой» промежуточный лист.
  if (yMm > 0.01 && p.bandHeightMm + CHUNKED_PDF_ROW_PAGE_SLACK_MM > remainingMm) {
    p.pdf.addPage();
    yMm = 0;
  }
  const imgData = p.canvas.toDataURL(`image/${p.imageType}`, p.imageQuality);
  p.pdf.addImage(imgData, p.imageType, p.marginLeft, p.marginTop + yMm, p.innerWidthMm, p.bandHeightMm);
  return yMm + p.bandHeightMm;
};

const PDF_REPEAT_LEADING_COLS_MAX = 32;

/** Суммарная ширина первых колонок [0..n) для уменьшения бюджета куска при повторе ведущих колонок. */
const getPrependedLeadingColumnWidthPx = (
  columnWidths: number[],
  colStart: number,
  repeatLeadingCount: number,
): number => {
  if (colStart <= 0 || repeatLeadingCount <= 0) {
    return 0;
  }
  const n = Math.min(repeatLeadingCount, colStart, columnWidths.length);
  let s = 0;
  for (let i = 0; i < n; i++) {
    s += columnWidths[i] ?? 0;
  }
  return s;
};

const parsePdfRepeatLeadingColumnCount = (table: HTMLTableElement): number => {
  const host = table.closest(`[${PDF_EXPORT_REPEAT_LEADING_COLS_ATTR}]`);
  if (!host?.hasAttribute(PDF_EXPORT_REPEAT_LEADING_COLS_ATTR)) {
    return 0;
  }
  const raw = host.getAttribute(PDF_EXPORT_REPEAT_LEADING_COLS_ATTR);
  if (raw === null || raw.trim() === '') {
    return 1;
  }
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n <= 0) {
    return 0;
  }
  return Math.min(Math.floor(n), PDF_REPEAT_LEADING_COLS_MAX);
};

type TTableCellInfo = {
  el: HTMLTableCellElement;
  row: number;
  col: number;
  rowspan: number;
  colspan: number;
};

/** TableAggregated: --has-colspan + --hidden-in-colspan → один td с реальным colSpan перед PDF-нарезкой. */
const normalizeLogicalColspanForPdfClone = (table: HTMLTableElement): void => {
  for (const tr of table.querySelectorAll<HTMLTableRowElement>('tbody tr, tfoot tr')) {
    const cells = Array.from(tr.cells);

    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      if (!cell.classList.contains('table-aggregated__row-cell--has-colspan')) {
        continue;
      }

      let span = 1;
      while (i + span < cells.length) {
        const next = cells[i + span];
        if (!next.classList.contains('table-aggregated__row-cell--hidden-in-colspan')) {
          break;
        }
        span += 1;
      }

      if (span <= 1) {
        continue;
      }

      cell.colSpan = span;
      cell.classList.remove('table-aggregated__row-cell--hidden-in-colspan');
      for (let j = span - 1; j >= 1; j--) {
        cells[i + j]?.remove();
      }
    }
  }
};

/** Пересечение [cellCol, cellCol+colspan) с диапазоном куска [colStart, colEnd). */
const getCellColSliceIntersection = (
  cellCol: number,
  cellColspan: number,
  colStart: number,
  colEndExclusive: number,
): { sliceColStart: number; sliceSpan: number } | null => {
  const sliceColStart = Math.max(cellCol, colStart);
  const sliceColEnd = Math.min(cellCol + cellColspan, colEndExclusive);
  if (sliceColStart >= sliceColEnd) {
    return null;
  }
  return { sliceColStart, sliceSpan: sliceColEnd - sliceColStart };
};

const PDF_SLICE_STYLE_MARKER = 'data-pdf-wide-table-slice-styles';

/** Зазор над thead в снимке html2canvas (px); не влияет на mm-пагинацию в fileExport. */
const PDF_SLICE_HEAD_TOP_GAP_PX = 5;

const ensurePdfWideTableSliceStyles = (root: HTMLElement): void => {
  if (root.querySelector(`[${PDF_SLICE_STYLE_MARKER}]`)) {
    return;
  }
  const style = document.createElement('style');
  style.setAttribute(PDF_SLICE_STYLE_MARKER, '1');
  style.textContent = `
    .pdf-wide-table-slice-chunk {
      display: block;
      margin: 0;
      padding: 0;
    }
    .pdf-wide-table-slice-chunk--continuation table {
      margin-top: 0 !important;
    }
    /* Переносы страниц — только поведение html2pdf + break-* ниже; без ручного расчета по высоте листа. */
    .pdf-wide-table-slice-chunk thead {
      display: table-header-group !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      break-after: auto !important;
      page-break-after: auto !important;
    }
    .pdf-wide-table-slice-chunk thead tr {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    /* Многострочная шапка: legacy + row-friendly не должны резать между tr thead */
    .pdf-wide-table-slice-chunk thead tr:not(:first-of-type) {
      break-before: avoid !important;
      page-break-before: avoid !important;
    }
    .pdf-wide-table-slice-chunk thead th {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .pdf-wide-table-slice-chunk tbody {
      display: table-row-group !important;
      break-inside: auto !important;
      page-break-inside: auto !important;
      break-inside: avoid-page !important;
      page-break-inside: avoid !important;
    }
    .pdf-wide-table-slice-chunk tbody tr {
      display: table-row !important;
      position: static !important;
      transform: none !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      break-inside: avoid-page !important;
      page-break-inside: avoid !important;
      break-before: avoid-page !important;
      page-break-before: avoid !important;
      break-after: auto !important;
      page-break-after: auto !important;
    }
    .pdf-wide-table-slice-chunk tbody td,
    .pdf-wide-table-slice-chunk tbody th {
      display: table-cell !important;
      position: static !important;
      left: auto !important;
      right: auto !important;
      inset: auto !important;
      inset-inline-start: auto !important;
      inset-inline-end: auto !important;
      transform: none !important;
      translate: none !important;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
      box-decoration-break: clone;
      -webkit-box-decoration-break: clone;
    }
    .pdf-wide-table-slice-chunk tbody td *,
    .pdf-wide-table-slice-chunk tbody th * {
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .pdf-wide-table-slice-chunk table {
      max-width: 100% !important;
    }
    .pdf-wide-table-slice-chunk .table-aggregated__header .table-aggregated__cell--fixed,
    .pdf-wide-table-slice-chunk .table-aggregated__cell--fixed,
    .pdf-wide-table-slice-chunk [class*="sticky"] {
      position: static !important;
      display: table-cell !important;
      left: auto !important;
      right: auto !important;
      inset: auto !important;
      inset-inline-start: auto !important;
      inset-inline-end: auto !important;
      margin-left: 0 !important;
      top: auto !important;
      transform: none !important;
      will-change: auto !important;
      z-index: auto !important;
    }
    /*
      Отдельная таблица только под thead: html2pdf (legacy + row-friendly) режет по tr внутри
      одной таблицы; вынесенная шапка целиком в блоке с break-inside: avoid переносится атомарно.
    */
    .pdf-wide-table-slice-chunk--split-head {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    .pdf-wide-table-slice-head-wrap {
      display: block;
      margin: 0;
      padding: ${PDF_SLICE_HEAD_TOP_GAP_PX}px 0 0;
      box-sizing: border-box;
      break-inside: avoid !important;
      page-break-inside: avoid !important;
    }
    .pdf-wide-table-slice-chunk:not(.pdf-wide-table-slice-chunk--split-head) > table > thead tr:first-child th,
    .pdf-wide-table-slice-chunk:not(.pdf-wide-table-slice-chunk--split-head) > table > thead tr:first-child td {
      padding-top: ${PDF_SLICE_HEAD_TOP_GAP_PX}px !important;
    }
    .pdf-wide-table-slice-head-wrap table {
      margin-left: 0 !important;
      margin-right: 0 !important;
      margin-bottom: 0 !important;
      border-collapse: collapse;
      width: auto !important;
      max-width: 100% !important;
    }
    .pdf-wide-table-slice-body-table {
      margin-top: 0 !important;
      border-collapse: collapse;
      width: auto !important;
      max-width: 100% !important;
      break-inside: auto !important;
      page-break-inside: auto !important;
    }
    .pdf-wide-table-slice-body-table > tbody {
      break-inside: avoid-page !important;
      page-break-inside: avoid !important;
    }
    .pdf-wide-table-slice-chunk + .pdf-wide-table-slice-chunk {
      margin-top: 20px !important; /* увеличиваем отступ между горизонтальными кусками */
      padding-top: 0 !important;
    }
  /* Правый край куска: таблица продолжается в следующем горизонтальном фрагменте (не для --line) */
    .pdf-wide-table-slice-chunk--has-right-continuation:not(.pdf-wide-table-slice-chunk--line-table)
      .pdf-wide-table-slice__cell-cut-edge {
      border-right: 1px solid rgba(0, 0, 0, 0.12) !important;
    }
  `;
  root.insertBefore(style, root.firstChild);
};

const PDF_SLICE_CUT_EDGE_CELL_CLASS = 'pdf-wide-table-slice__cell-cut-edge';

/** TableAggregated без рамок по периметру — только горизонтальные линии между строками. */
const isTableAggregatedLineMode = (table: HTMLTableElement): boolean =>
  table.classList.contains('table-aggregated--line');

const resolvePdfSliceCellRightBorder = (sample: HTMLTableElement): string => {
  const sampleCell = sample.querySelector<HTMLElement>('tbody td.table-aggregated__row-cell, tbody th, thead th');
  if (!sampleCell) {
    return '1px solid rgba(0, 0, 0, 0.12)';
  }
  const cs = window.getComputedStyle(sampleCell);
  if (cs.borderRightStyle && cs.borderRightStyle !== 'none' && cs.borderRightWidth !== '0px') {
    return cs.borderRight;
  }
  return '1px solid rgba(0, 0, 0, 0.12)';
};

/** Последняя ячейка каждой строки — визуальная граница перед следующим горизонтальным куском. */
const markPdfChunkHorizontalCutEdgeCells = (wrapper: HTMLElement, sample: HTMLTableElement): void => {
  if (isTableAggregatedLineMode(sample)) {
    return;
  }

  const borderRight = resolvePdfSliceCellRightBorder(sample);
  for (const table of wrapper.querySelectorAll<HTMLTableElement>('table')) {
    for (const tr of table.querySelectorAll<HTMLTableRowElement>('thead tr, tbody tr, tfoot tr')) {
      const lastCell = tr.cells.item(tr.cells.length - 1);
      if (!lastCell) {
        continue;
      }
      lastCell.classList.add(PDF_SLICE_CUT_EDGE_CELL_CLASS);
      lastCell.style.setProperty('border-right', borderRight, 'important');
    }
  }
};

/** Верхняя граница таблицы в PDF (после пересборки thead border-collapse часто «съедает» линию) */
const applyTableTopBorderForPdfSlice = (
  copy: HTMLTableElement,
  sample: HTMLTableElement,
  withTopBorder: boolean,
): void => {
  if (!withTopBorder) {
    copy.style.borderTop = 'none';
    return;
  }
  const sampleCs = window.getComputedStyle(sample);
  if (sampleCs.borderTopStyle && sampleCs.borderTopStyle !== 'none' && sampleCs.borderTopWidth !== '0px') {
    copy.style.borderTop = sampleCs.borderTop;
    return;
  }
  const firstTh = sample.querySelector('thead th');
  if (firstTh) {
    const thCs = window.getComputedStyle(firstTh);
    if (thCs.borderTopStyle && thCs.borderTopStyle !== 'none' && thCs.borderTopWidth !== '0px') {
      copy.style.borderTop = thCs.borderTop;
      return;
    }
  }
  copy.style.borderTop = '1px solid rgba(0, 0, 0, 0.12)';
};

const stripVirtualizedHeightsForPdf = (table: HTMLTableElement): void => {
  table.style.removeProperty('height');
  table.style.removeProperty('min-height');
  const tbody = table.querySelector('tbody');
  if (tbody) {
    tbody.style.removeProperty('height');
    tbody.style.removeProperty('min-height');
  }
};

/**
 * Сброс offset-стилей виртуализированных строк/ячеек TableAggregated.
 * Критично для кейса, когда первая колонка получает inline left/right и уезжает вправо в PDF.
 */
const normalizeVirtualizedOffsetsForPdf = (root: ParentNode): void => {
  const rows = Array.from(root.querySelectorAll<HTMLElement>('.table-aggregated__row'));
  for (const row of rows) {
    row.style.setProperty('position', 'static', 'important');
    row.style.setProperty('left', 'auto', 'important');
    row.style.setProperty('right', 'auto', 'important');
    row.style.setProperty('top', 'auto', 'important');
    row.style.setProperty('bottom', 'auto', 'important');
    row.style.setProperty('inset', 'auto', 'important');
    row.style.setProperty('inset-inline-start', 'auto', 'important');
    row.style.setProperty('inset-inline-end', 'auto', 'important');
    row.style.setProperty('transform', 'none', 'important');
    row.style.setProperty('translate', 'none', 'important');
    row.style.setProperty('width', 'auto', 'important');
  }

  const cells = Array.from(
    root.querySelectorAll<HTMLElement>('.table-aggregated__row-cell, .table-aggregated__cell--fixed'),
  );
  for (const cell of cells) {
    cell.style.setProperty('position', 'static', 'important');
    cell.style.setProperty('left', 'auto', 'important');
    cell.style.setProperty('right', 'auto', 'important');
    cell.style.setProperty('top', 'auto', 'important');
    cell.style.setProperty('bottom', 'auto', 'important');
    cell.style.setProperty('inset', 'auto', 'important');
    cell.style.setProperty('inset-inline-start', 'auto', 'important');
    cell.style.setProperty('inset-inline-end', 'auto', 'important');
    cell.style.setProperty('transform', 'none', 'important');
    cell.style.setProperty('translate', 'none', 'important');
    cell.style.setProperty('z-index', 'auto', 'important');
    cell.style.setProperty('margin-left', '0', 'important');
  }
};

/** Для body-части таблицы сбрасываем любые координатные смещения у строк/ячеек. */
const normalizeBodyCellPositionForPdf = (table: HTMLTableElement): void => {
  const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr'));
  for (const row of rows) {
    row.style.setProperty('position', 'static', 'important');
    row.style.setProperty('transform', 'none', 'important');
    row.style.setProperty('translate', 'none', 'important');
    row.style.setProperty('left', 'auto', 'important');
    row.style.setProperty('right', 'auto', 'important');
    row.style.setProperty('inset', 'auto', 'important');
  }

  const cells = Array.from(table.querySelectorAll<HTMLElement>('tbody td, tbody th'));
  for (const cell of cells) {
    cell.style.setProperty('display', 'table-cell', 'important');
    cell.style.setProperty('position', 'static', 'important');
    cell.style.setProperty('left', 'auto', 'important');
    cell.style.setProperty('right', 'auto', 'important');
    cell.style.setProperty('inset', 'auto', 'important');
    cell.style.setProperty('inset-inline-start', 'auto', 'important');
    cell.style.setProperty('inset-inline-end', 'auto', 'important');
    cell.style.setProperty('transform', 'none', 'important');
    cell.style.setProperty('translate', 'none', 'important');
    cell.style.setProperty('margin-left', '0', 'important');
  }
};

/**
 * Только инлайн-смещение sticky-колонок TableAggregated (шапка + тело).
 * Не трогаем строки виртуализации и высоты — иначе ломаются переносы страниц в html2pdf.
 */
const resetTableAggregatedFixedColumnOffsetsForPdfClone = (root: ParentNode): void => {
  const cells = root.querySelectorAll<HTMLElement>('.table-aggregated__cell--fixed');
  for (const el of cells) {
    el.style.setProperty('position', 'static', 'important');
    el.style.setProperty('left', 'auto', 'important');
    el.style.setProperty('right', 'auto', 'important');
    el.style.setProperty('inset', 'auto', 'important');
    el.style.setProperty('inset-inline-start', 'auto', 'important');
    el.style.setProperty('inset-inline-end', 'auto', 'important');
    el.style.setProperty('top', 'auto', 'important');
    el.style.setProperty('transform', 'none', 'important');
    el.style.setProperty('translate', 'none', 'important');
    el.style.setProperty('margin-left', '0', 'important');
    el.style.setProperty('z-index', 'auto', 'important');
  }
};

/** Ширина блока шапки = сумме колонок куска (как у body после applyPdfChunkColumnWidths). */
const syncPdfSliceHeadBlockWidthToBody = (wrapper: HTMLElement, totalWidthPx: number): void => {
  const headWrap = wrapper.querySelector<HTMLElement>('.pdf-wide-table-slice-head-wrap');
  const headTable = headWrap?.querySelector<HTMLTableElement>('table');
  if (!headWrap || !headTable || totalWidthPx <= 0) {
    return;
  }

  const px = `${totalWidthPx}px`;
  headWrap.style.setProperty('width', px, 'important');
  headTable.style.setProperty('width', px, 'important');
  headTable.style.setProperty('max-width', px, 'important');
};

/** Замеряем точную высоту tr как она будет рендериться в html2pdf после stripVirtualizedHeightsForPdf */
const measureSectionRowHeightsPx = (
  table: HTMLTableElement,
  sectionSelector: 'thead' | 'tbody' | 'tfoot',
): Map<number, number> => {
  const map = new Map<number, number>();
  const rows = table.querySelectorAll<HTMLTableRowElement>(`${sectionSelector} tr`);
  rows.forEach((tr, idx) => {
    const h = tr.getBoundingClientRect().height;
    if (h > 0) {
      map.set(idx, h);
    }
  });
  return map;
};

const applySectionRowExactHeightsPx = (
  table: HTMLTableElement,
  sectionSelector: 'thead' | 'tbody' | 'tfoot',
  heights: Map<number, number>,
): void => {
  const rows = table.querySelectorAll<HTMLTableRowElement>(`${sectionSelector} tr`);
  rows.forEach((tr, idx) => {
    const h = heights.get(idx);
    if (!h || h <= 0) return;
    tr.style.height = `${h}px`;
    tr.style.minHeight = `${h}px`;
    tr.style.maxHeight = `${h}px`;

    // Иногда html2pdf "подтягивает" высоту из ячеек, фиксируем и их.
    const cells = tr.querySelectorAll<HTMLElement>('th, td');
    cells.forEach((cell) => {
      cell.style.height = `${h}px`;
      cell.style.minHeight = `${h}px`;
      cell.style.maxHeight = `${h}px`;
    });
  });
};

const mountCloneForMeasure = (root: HTMLElement, layoutWidthPx: number): HTMLDivElement => {
  const host = document.createElement('div');
  host.style.cssText = [
    'position:fixed',
    'left:0',
    'top:0',
    `width:${layoutWidthPx}px`,
    'opacity:0',
    'pointer-events:none',
    'z-index:-1',
    'overflow:visible',
  ].join(';');
  host.appendChild(root);
  document.body.appendChild(host);
  return host;
};

/** Временно intrinsic-ширина таблицы, иначе в узком хосте колонки сжимаются и нарезка не срабатывает */
const withIntrinsicTableWidth = (table: HTMLTableElement): (() => void) => {
  const prevWidth = table.style.width;
  const prevMinWidth = table.style.minWidth;
  table.style.setProperty('width', 'max-content');
  table.style.setProperty('min-width', 'max-content');
  table.offsetWidth;
  return () => {
    if (prevWidth) {
      table.style.width = prevWidth;
    } else {
      table.style.removeProperty('width');
    }
    if (prevMinWidth) {
      table.style.minWidth = prevMinWidth;
    } else {
      table.style.removeProperty('min-width');
    }
    table.offsetWidth;
  };
};

/**
 * HTML5: rowspan="0" — ячейка тянется до конца текущей секции (thead|tbody|tfoot).
 * В DOM rowSpan при этом 0; без подстановки сетка колонок ломается (ячейка «Страна» исчезает из грида).
 */
const getEffectiveRowSpanForGrid = (
  cell: HTMLTableCellElement,
  tableRowIndex: number,
  tableNumRows: number,
): number => {
  const raw = cell.rowSpan;
  if (raw !== 0) {
    return Math.max(1, raw);
  }

  // В TableAggregated "продолжение" rowspan часто представлено ячейкой с `rowSpan=0` и классом
  // `--hidden-in-rowspan`. Эти ячейки не должны тянуться до конца секции, иначе:
  // - buildCellInfos начинает считать их "перекрывающими" другие строки,
  // - при повторе ведущих колонок пропадает корректная bottom-грань у `--last-in-rowspan`,
  // - и может теряться последняя строка на границе печати.
  if (cell.classList.contains('table-aggregated__row-cell--hidden-in-rowspan')) {
    return 1;
  }

  const section = cell.closest('thead') ?? cell.closest('tbody') ?? cell.closest('tfoot');
  if (!section) {
    return Math.max(1, tableNumRows - tableRowIndex);
  }

  const tr = cell.parentElement;
  if (!(tr instanceof HTMLTableRowElement)) {
    return 1;
  }

  const sectionRows = Array.from(section.rows);
  const localR = sectionRows.indexOf(tr);
  if (localR < 0) {
    return 1;
  }

  return Math.max(1, section.rows.length - localR);
};

const buildCellInfos = (table: HTMLTableElement): TTableCellInfo[] => {
  const numRows = table.rows.length;
  if (!numRows) {
    return [];
  }

  let numCols = 0;
  for (let r = 0; r < numRows; r++) {
    let c = 0;
    for (const cell of table.rows[r].cells) {
      c += cell.colSpan;
    }
    numCols = Math.max(numCols, c);
  }

  const grid: (HTMLTableCellElement | null)[][] = Array.from({ length: numRows }, () => Array(numCols).fill(null));

  for (let r = 0; r < numRows; r++) {
    let c = 0;
    const row = table.rows[r];
    for (const cell of row.cells) {
      while (c < numCols && grid[r][c]) {
        c++;
      }
      const rowSpan = getEffectiveRowSpanForGrid(cell, r, numRows);
      for (let dr = 0; dr < rowSpan; dr++) {
        for (let dc = 0; dc < cell.colSpan; dc++) {
          if (r + dr < numRows && c + dc < numCols) {
            grid[r + dr][c + dc] = cell;
          }
        }
      }
      c += cell.colSpan;
    }
  }

  const seen = new Set<HTMLTableCellElement>();
  const infos: TTableCellInfo[] = [];

  for (let r = 0; r < numRows; r++) {
    for (let c = 0; c < numCols; c++) {
      const el = grid[r][c];
      if (!el || seen.has(el)) {
        continue;
      }
      seen.add(el);

      let minR = r;
      let minC = c;
      let maxR = r;
      let maxC = c;
      for (let rr = 0; rr < numRows; rr++) {
        for (let cc = 0; cc < numCols; cc++) {
          if (grid[rr][cc] === el) {
            minR = Math.min(minR, rr);
            minC = Math.min(minC, cc);
            maxR = Math.max(maxR, rr);
            maxC = Math.max(maxC, cc);
          }
        }
      }

      infos.push({
        el,
        row: minR,
        col: minC,
        rowspan: maxR - minR + 1,
        colspan: maxC - minC + 1,
      });
    }
  }

  return infos;
};

const measureColumnWidths = (table: HTMLTableElement, columnCount: number, cellInfos: TTableCellInfo[]): number[] => {
  const widths = Array(columnCount).fill(0);

  for (const { el, col, colspan } of cellInfos) {
    const w = el.getBoundingClientRect().width;
    const share = w / colspan;
    for (let k = 0; k < colspan; k++) {
      const idx = col + k;
      if (idx < columnCount) {
        widths[idx] = Math.max(widths[idx], share);
      }
    }
  }

  const tableWidth = table.getBoundingClientRect().width;
  const sum = widths.reduce((a, b) => a + b, 0);
  if (sum > 0 && tableWidth > 0) {
    const scale = tableWidth / sum;
    for (let i = 0; i < columnCount; i++) {
      widths[i] *= scale;
    }
  }

  return widths;
};

/**
 * Можно ли вынести thead в соседнюю таблицу: иначе rowspan из шапки в tbody ломает сетку.
 * caption не трогаем — при наличии остаёмся в одной таблице (caption должен быть первым ребёнком table).
 */
const canTheadBeDetachedToSiblingTable = (table: HTMLTableElement): boolean => {
  const thead = table.querySelector('thead');
  if (!thead || table.querySelector('caption')) {
    return false;
  }

  const allRows = Array.from(table.rows);
  let theadLastGlobalIndex = -1;
  for (const r of thead.rows) {
    const idx = allRows.indexOf(r);
    if (idx >= 0) {
      theadLastGlobalIndex = Math.max(theadLastGlobalIndex, idx);
    }
  }
  if (theadLastGlobalIndex < 0) {
    return false;
  }

  for (const cell of thead.querySelectorAll<HTMLTableCellElement>('th, td')) {
    const tr = cell.closest('tr');
    if (!tr || !thead.contains(tr)) {
      continue;
    }
    const rowIdx = allRows.indexOf(tr);
    if (rowIdx < 0) {
      continue;
    }
    const rs = cell.rowSpan > 0 ? cell.rowSpan : 1;
    const lastSpanned = rowIdx + rs - 1;
    if (lastSpanned > theadLastGlobalIndex) {
      return false;
    }
  }

  return true;
};

const captureBodyCellTextForPdfSlice = (cellInfos: TTableCellInfo[]): Map<HTMLTableCellElement, string> => {
  const map = new Map<HTMLTableCellElement, string>();
  for (const { el } of cellInfos) {
    if (el.closest('tbody') || el.closest('tfoot')) {
      map.set(el, (el.textContent ?? '').replace(/\s+/g, ' ').trim());
    }
  }
  return map;
};

/** Обрезка tbody/tfoot/thead по [colStart, colEnd) без пересборки tr (сохраняет порядок DOM). */
const sliceTableToColumnRange = (
  table: HTMLTableElement,
  colStart: number,
  colEndExclusive: number,
  options?: { skipThead?: boolean },
): void => {
  const cellInfos = buildCellInfos(table);
  const cellTextByEl = captureBodyCellTextForPdfSlice(cellInfos);

  for (const { el, col, colspan } of cellInfos) {
    if (options?.skipThead && el.closest('thead')) {
      continue;
    }

    const slice = getCellColSliceIntersection(col, colspan, colStart, colEndExclusive);
    if (!slice) {
      el.remove();
      continue;
    }

    if (slice.sliceSpan !== colspan) {
      el.colSpan = slice.sliceSpan;
    }

    if (el.closest('tbody') || el.closest('tfoot')) {
      const sourceText = cellTextByEl.get(el) ?? '';
      if (col < colStart) {
        el.classList.add('pdf-wide-table-slice__cell-continuation');
        el.replaceChildren(document.createTextNode(sourceText || '\u00a0'));
      } else {
        el.classList.remove('pdf-wide-table-slice__cell-continuation');
      }
    }
  }

  for (const tr of Array.from(table.querySelectorAll<HTMLTableRowElement>('tbody tr, tfoot tr, thead tr'))) {
    if (!tr.cells.length) {
      tr.remove();
    }
  }
};

type TPdfChunkColumnWidthContext = {
  columnWidths: number[];
  colStart: number;
  colEndExclusive: number;
  leadPrefixCols: number;
};

/** Индекс колонки в исходной таблице по позиции ячейки в куске (после prepend ведущих колонок сетка идёт подряд). */
const resolvePdfChunkSourceColumnIndex = (gridCol: number, ctx: TPdfChunkColumnWidthContext): number => {
  const { colStart, leadPrefixCols } = ctx;
  if (leadPrefixCols > 0 && colStart > 0) {
    if (gridCol < leadPrefixCols) {
      return gridCol;
    }
    return colStart + (gridCol - leadPrefixCols);
  }
  if (colStart > 0) {
    return colStart + gridCol;
  }
  return gridCol;
};

const getPdfChunkCellWidthPx = (gridCol: number, colspan: number, ctx: TPdfChunkColumnWidthContext): number => {
  let sum = 0;
  for (let k = 0; k < colspan; k++) {
    const src = resolvePdfChunkSourceColumnIndex(gridCol + k, ctx);
    sum += ctx.columnWidths[src] ?? 0;
  }
  return sum;
};

const getPdfChunkTableTotalWidthPx = (ctx: TPdfChunkColumnWidthContext): number => {
  const { columnWidths, colStart, colEndExclusive, leadPrefixCols } = ctx;
  if (leadPrefixCols > 0 && colStart > 0) {
    let sum = 0;
    for (let i = 0; i < leadPrefixCols; i++) {
      sum += columnWidths[i] ?? 0;
    }
    for (let i = colStart; i < colEndExclusive; i++) {
      sum += columnWidths[i] ?? 0;
    }
    return sum;
  }
  let sum = 0;
  for (let i = colStart; i < colEndExclusive; i++) {
    sum += columnWidths[i] ?? 0;
  }
  return sum;
};

const applyPdfChunkColumnWidths = (table: HTMLTableElement, ctx: TPdfChunkColumnWidthContext): void => {
  const totalW = getPdfChunkTableTotalWidthPx(ctx);
  if (totalW <= 0) {
    return;
  }

  table.style.tableLayout = 'fixed';
  table.style.width = `${totalW}px`;
  table.style.minWidth = `${totalW}px`;
  table.style.maxWidth = `${totalW}px`;

  for (const { el, col, colspan } of buildCellInfos(table)) {
    const w = getPdfChunkCellWidthPx(col, colspan, ctx);
    if (w <= 0) {
      continue;
    }
    const px = `${w}px`;
    el.style.width = px;
    el.style.minWidth = px;
    el.style.maxWidth = px;
    el.style.boxSizing = 'border-box';
  }
};

const appendTheadFillerTh = (tr: HTMLTableRowElement, colSpan: number): void => {
  const filler = document.createElement('th');
  filler.colSpan = colSpan;
  filler.className = 'pdf-wide-table-slice__thead-filler';
  filler.appendChild(document.createTextNode('\u00a0'));
  tr.appendChild(filler);
};

const groupHeadCellInfosByRow = (headInfos: TTableCellInfo[]): Map<number, TTableCellInfo[]> => {
  const byRow = new Map<number, TTableCellInfo[]>();
  for (const info of headInfos) {
    const list = byRow.get(info.row) ?? [];
    list.push(info);
    byRow.set(info.row, list);
  }
  return byRow;
};

const getTheadCellSliceIntersection = (
  cell: TTableCellInfo,
  colStart: number,
  colEndExclusive: number,
): { sliceStart: number; sliceSpan: number } | null => {
  const slice = getCellColSliceIntersection(cell.col, cell.colspan, colStart, colEndExclusive);
  if (!slice) {
    return null;
  }
  return { sliceStart: slice.sliceColStart - colStart, sliceSpan: slice.sliceSpan };
};

/** Заполняет слоты [from, until) в срезе: либо списывает rowSpan сверху, либо вставляет filler-th. */
const fillTheadRowSpanSlotsRange = (
  tr: HTMLTableRowElement,
  rowSpanBelow: Uint8Array,
  from: number,
  until: number,
): number => {
  let s = from;
  while (s < until) {
    if (rowSpanBelow[s] > 0) {
      rowSpanBelow[s] -= 1;
    } else {
      appendTheadFillerTh(tr, 1);
    }
    s += 1;
  }
  return s;
};

const appendClonedTheadCellForSlice = (
  tr: HTMLTableRowElement,
  cell: TTableCellInfo,
  sliceStart: number,
  sliceSpan: number,
  sliceW: number,
  rowSpanBelow: Uint8Array,
  wrapRowCount: number,
  sourceHeadRowCount: number,
  rowIdx: number,
): void => {
  const th = cell.el.cloneNode(true) as HTMLTableCellElement;
  th.colSpan = sliceSpan;
  const effRs = getEffectiveRowSpanForGrid(cell.el, cell.row, wrapRowCount);
  const maxRs = sourceHeadRowCount - rowIdx;
  th.rowSpan = Math.min(effRs, maxRs);
  const rsBelow = th.rowSpan - 1;
  for (let k = 0; k < sliceSpan; k++) {
    const slot = sliceStart + k;
    if (slot < sliceW) {
      rowSpanBelow[slot] = Math.max(rowSpanBelow[slot], rsBelow);
    }
  }
  tr.appendChild(th);
};

const buildSingleSlicedTheadRow = (
  rowIdx: number,
  sourceRowList: HTMLTableRowElement[],
  byRow: Map<number, TTableCellInfo[]>,
  colStart: number,
  colEndExclusive: number,
  sliceW: number,
  rowSpanBelow: Uint8Array,
  wrap: HTMLTableElement,
): HTMLTableRowElement | null => {
  const rowCells = (byRow.get(rowIdx) ?? []).sort((a, b) => a.col - b.col);
  const tr = document.createElement('tr');
  const srcTr = sourceRowList[rowIdx];
  if (srcTr) {
    tr.className = srcTr.className;
  }

  let s = 0;
  for (const cell of rowCells) {
    const slice = getTheadCellSliceIntersection(cell, colStart, colEndExclusive);
    if (!slice) {
      continue;
    }

    s = fillTheadRowSpanSlotsRange(tr, rowSpanBelow, s, slice.sliceStart);

    appendClonedTheadCellForSlice(
      tr,
      cell,
      slice.sliceStart,
      slice.sliceSpan,
      sliceW,
      rowSpanBelow,
      wrap.rows.length,
      sourceRowList.length,
      rowIdx,
    );
    s = slice.sliceStart + slice.sliceSpan;
  }

  fillTheadRowSpanSlotsRange(tr, rowSpanBelow, s, sliceW);

  return tr.cells.length > 0 ? tr : null;
};

/**
 * Собирает thead под срез [colStart, colEnd), с учетом rowSpan: не вставляет filler
 * в слоты, закрытые вертикальным объединением с предыдущих строк (типично первая колонка + 2 ряда шапки).
 */
const buildSlicedTheadFromSource = (
  sourceThead: HTMLTableSectionElement,
  colStart: number,
  colEndExclusive: number,
): HTMLTableSectionElement => {
  const wrap = document.createElement('table');
  wrap.appendChild(sourceThead.cloneNode(true));
  wrap.appendChild(document.createElement('tbody'));
  const headInfos = buildCellInfos(wrap).filter((i) => i.el.closest('thead'));
  const byRow = groupHeadCellInfosByRow(headInfos);

  const sliceW = colEndExclusive - colStart;
  const rowSpanBelow = new Uint8Array(sliceW);
  const newThead = document.createElement('thead');
  const sourceRowList = Array.from(sourceThead.rows);

  for (let rowIdx = 0; rowIdx < sourceRowList.length; rowIdx++) {
    const tr = buildSingleSlicedTheadRow(
      rowIdx,
      sourceRowList,
      byRow,
      colStart,
      colEndExclusive,
      sliceW,
      rowSpanBelow,
      wrap,
    );
    if (tr) {
      newThead.appendChild(tr);
    }
  }

  return newThead;
};

/**
 * На каждый горизонтальный кусок — та же шапка, что в sourceThead, но обрезанная/дублированная
 * по пересечению с [colStart, colEnd) + выравнивающие пустые th (без filler под rowSpan).
 */
const replaceTheadWithSliceDuplicate = (
  targetThead: HTMLTableSectionElement,
  colStart: number,
  colEndExclusive: number,
  sourceThead: HTMLTableSectionElement,
): void => {
  const newThead = buildSlicedTheadFromSource(sourceThead, colStart, colEndExclusive);
  targetThead.replaceWith(newThead);
};

/**
 * Шапка: ведущие колонки + срез куска. Строки совмещаются по индексу исходного thead, а не по
 * индексу в уже нарезанных thead (у rowspan ведущей колонки меньше tr, чем у групповых заголовков).
 */
const buildMergedTheadWithLeadingColumns = (
  sourceThead: HTMLTableSectionElement,
  leadStart: number,
  leadEndExclusive: number,
  bodyStart: number,
  bodyEndExclusive: number,
): HTMLTableSectionElement => {
  const wrap = document.createElement('table');
  wrap.appendChild(sourceThead.cloneNode(true));
  wrap.appendChild(document.createElement('tbody'));
  const headInfos = buildCellInfos(wrap).filter((i) => i.el.closest('thead'));
  const byRow = groupHeadCellInfosByRow(headInfos);

  const leadSliceW = leadEndExclusive - leadStart;
  const bodySliceW = bodyEndExclusive - bodyStart;
  const leadRowSpanBelow = new Uint8Array(leadSliceW);
  const bodyRowSpanBelow = new Uint8Array(bodySliceW);
  const merged = document.createElement('thead');
  const sourceRowList = Array.from(sourceThead.rows);

  for (let rowIdx = 0; rowIdx < sourceRowList.length; rowIdx++) {
    const trLead = buildSingleSlicedTheadRow(
      rowIdx,
      sourceRowList,
      byRow,
      leadStart,
      leadEndExclusive,
      leadSliceW,
      leadRowSpanBelow,
      wrap,
    );
    const trBody = buildSingleSlicedTheadRow(
      rowIdx,
      sourceRowList,
      byRow,
      bodyStart,
      bodyEndExclusive,
      bodySliceW,
      bodyRowSpanBelow,
      wrap,
    );

    if (!trLead && !trBody) {
      continue;
    }

    const tr = document.createElement('tr');
    const srcTr = sourceRowList[rowIdx];
    if (srcTr) {
      tr.className = srcTr.className;
    }

    if (trLead) {
      for (const cell of Array.from(trLead.cells)) {
        tr.appendChild(cell);
      }
    }
    if (trBody) {
      for (const cell of Array.from(trBody.cells)) {
        tr.appendChild(cell);
      }
    }

    merged.appendChild(tr);
  }

  return merged;
};

/** Клоны ячеек tbody/tfoot, полностью входящих в [rangeStart, rangeEnd), по строкам section. */
const extractLeadingSectionCellClonesByRows = (
  table: HTMLTableElement,
  section: HTMLTableSectionElement,
  rangeStart: number,
  rangeEndExclusive: number,
): HTMLTableCellElement[][] => {
  const globalRows = Array.from(table.rows);
  const infos = buildCellInfos(table).filter((i) => section.contains(i.el));

  return Array.from(section.rows).map((tr) => {
    const rowGlobalIdx = globalRows.indexOf(tr);
    const rowCells = infos
      .filter((i) => i.row === rowGlobalIdx && i.col >= rangeStart && i.col + i.colspan <= rangeEndExclusive)
      .sort((a, b) => a.col - b.col);
    return rowCells.map((i) => i.el.cloneNode(true) as HTMLTableCellElement);
  });
};

const extractAllTbodyLeadingClonesForPdfChunk = (
  table: HTMLTableElement,
  leadPrefixCols: number,
): HTMLTableCellElement[][] => {
  const rows: HTMLTableCellElement[][] = [];
  for (const tb of Array.from(table.tBodies)) {
    rows.push(...extractLeadingSectionCellClonesByRows(table, tb, 0, leadPrefixCols));
  }
  return rows;
};

const extractAllTfootLeadingClonesForPdfChunk = (
  table: HTMLTableElement,
  leadPrefixCols: number,
): HTMLTableCellElement[][] => {
  const tf = table.tFoot;
  if (!tf) {
    return [];
  }
  return extractLeadingSectionCellClonesByRows(table, tf, 0, leadPrefixCols);
};

const prependLeadingClonesToTbodyRows = (table: HTMLTableElement, rowClones: HTMLTableCellElement[][]): void => {
  let offset = 0;
  for (const tb of Array.from(table.tBodies)) {
    Array.from(tb.rows).forEach((tr, i) => {
      const clones = rowClones[offset + i];
      if (!clones?.length) {
        return;
      }
      for (let j = clones.length - 1; j >= 0; j--) {
        tr.insertBefore(clones[j], tr.firstChild);
      }
    });
    offset += tb.rows.length;
  }
};

const prependLeadingClonesToTfootRows = (table: HTMLTableElement, rowClones: HTMLTableCellElement[][]): void => {
  const tf = table.tFoot;
  if (!tf || rowClones.length === 0) {
    return;
  }
  Array.from(tf.rows).forEach((tr, i) => {
    const clones = rowClones[i];
    if (!clones?.length) {
      return;
    }
    for (let j = clones.length - 1; j >= 0; j--) {
      tr.insertBefore(clones[j], tr.firstChild);
    }
  });
};

/** Правый конец куска [start, end) по сумме ширин колонок; colspan в body обрезается при slice. */
const findWidthBasedChunkEnd = (
  start: number,
  columnCount: number,
  columnWidths: number[],
  maxWidthPx: number,
): number => {
  let sum = 0;
  let bestEnd = start;

  for (let k = start; k < columnCount; k++) {
    sum += columnWidths[k] ?? 0;
    if (sum > maxWidthPx) {
      break;
    }
    bestEnd = k + 1;
  }

  return bestEnd;
};

const computeChunks = (
  columnCount: number,
  columnWidths: number[],
  maxWidthPx: number,
  repeatLeadingCount: number,
): [number, number][] => {
  const chunks: [number, number][] = [];
  let start = 0;

  while (start < columnCount) {
    const prepended = getPrependedLeadingColumnWidthPx(columnWidths, start, repeatLeadingCount);
    const chunkBudget = start > 0 && prepended > 0 ? Math.max(200, maxWidthPx - prepended) : maxWidthPx;

    let bestEnd = findWidthBasedChunkEnd(start, columnCount, columnWidths, chunkBudget);

    if (bestEnd <= start) {
      bestEnd = Math.min(start + 1, columnCount);
    }

    chunks.push([start, bestEnd]);
    start = bestEnd;
  }

  return chunks;
};

const filterTopLevelTables = (tables: HTMLTableElement[]): HTMLTableElement[] =>
  tables.filter((table) => !tables.some((other) => other !== table && other.contains(table)));

const isWideTablePdfSplitDisabled = (table: HTMLTableElement): boolean =>
  table.closest(`[${PDF_EXPORT_NO_WIDE_TABLE_SPLIT_ATTR}]`) !== null;

/** Таблицы с меткой «без нарезки»: сбрасываем full-width растяжение в клоне, чтобы в PDF влезали как на экране. */
const prepareNoWideSplitTableForPdfExport = (table: HTMLTableElement): void => {
  stripVirtualizedHeightsForPdf(table);
  normalizeVirtualizedOffsetsForPdf(table);
  normalizeBodyCellPositionForPdf(table);

  const releaseIntrinsic = withIntrinsicTableWidth(table);
  try {
    table.style.maxWidth = '100%';

    const chain = [
      table.closest<HTMLElement>('.table-aggregated-section__content'),
      table.closest<HTMLElement>('.table-aggregated-section__wrapper'),
      table.closest<HTMLElement>('.table-aggregated-section'),
      table.closest<HTMLElement>('.analytics-notes-import-export-table'),
    ];

    for (const el of chain) {
      if (!el) {
        continue;
      }
      el.style.width = 'max-content';
      el.style.maxWidth = '100%';
    }
  } finally {
    releaseIntrinsic();
  }
};

export type TSplitWideTablesForPdfCloneOptions = {
  maxWidthPx: number;
  tableSelector?: string;
  /** Совпадает с html2canvas.windowWidth в downloadPDF */
  layoutWidthPx?: number;
  /** Верхняя граница у горизонтальных кусков после нарезки (по умолчанию true) */
  withTopBorder?: boolean;
};

type TWideTableChunkRange = [number, number];

type TWideTableSplitPlan = {
  chunks: TWideTableChunkRange[];
  columnWidths: number[];
};

const tryComputeWideTableSplitPlan = (table: HTMLTableElement, maxWidthPx: number): TWideTableSplitPlan | null => {
  const probe = table.cloneNode(true) as HTMLTableElement;
  stripVirtualizedHeightsForPdf(probe);
  normalizeLogicalColspanForPdfClone(probe);

  const mountParent = table.parentElement;
  if (mountParent) {
    probe.style.cssText = 'position:fixed;left:-10000px;top:0;visibility:hidden;pointer-events:none;';
    mountParent.appendChild(probe);
  }

  try {
    const cellInfos = buildCellInfos(probe);
    if (!cellInfos.length) {
      return null;
    }

    const columnCount = cellInfos.reduce((m, i) => Math.max(m, i.col + i.colspan), 0);
    if (columnCount <= 0) {
      return null;
    }

    const columnWidths = measureColumnWidths(probe, columnCount, cellInfos);
    const sumMeasuredWidths = columnWidths.reduce((a, b) => a + b, 0);
    const intrinsicCandidate = Math.max(probe.scrollWidth, probe.offsetWidth, Math.round(sumMeasuredWidths));

    if (intrinsicCandidate <= maxWidthPx + 1) {
      return null;
    }

    const repeatLeadingCount = parsePdfRepeatLeadingColumnCount(table);
    const chunks = computeChunks(columnCount, columnWidths, maxWidthPx, repeatLeadingCount);

    if (chunks.length <= 1) {
      return null;
    }

    return { chunks, columnWidths };
  } finally {
    probe.remove();
  }
};

const cloneColgroupsIntoHeadTable = (bodyTable: HTMLTableElement, headTable: HTMLTableElement): void => {
  for (const child of Array.from(bodyTable.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE && (child as Element).tagName === 'COLGROUP') {
      headTable.appendChild((child as Element).cloneNode(true));
    }
  }
};

const appendSplitHeadSliceToWrapper = (
  wrapper: HTMLDivElement,
  bodyTable: HTMLTableElement,
  theadEl: HTMLTableSectionElement,
  sampleTable: HTMLTableElement,
  withTopBorder: boolean,
): void => {
  wrapper.classList.add('pdf-wide-table-slice-chunk--split-head');

  const headWrap = document.createElement('div');
  headWrap.className = 'pdf-wide-table-slice-head-wrap';

  const headTable = document.createElement('table');
  headTable.className = bodyTable.className;

  cloneColgroupsIntoHeadTable(bodyTable, headTable);

  headTable.appendChild(theadEl);
  stripVirtualizedHeightsForPdf(headTable);
  normalizeVirtualizedOffsetsForPdf(headTable);
  headWrap.appendChild(headTable);

  bodyTable.classList.add('pdf-wide-table-slice-body-table');
  normalizeVirtualizedOffsetsForPdf(bodyTable);

  applyTableTopBorderForPdfSlice(headTable, sampleTable, withTopBorder);
  applyTableTopBorderForPdfSlice(bodyTable, sampleTable, false);

  wrapper.appendChild(headWrap);
  wrapper.appendChild(bodyTable);
};

type TBuildWideTableChunkWrapperParams = {
  sourceTable: HTMLTableElement;
  colStart: number;
  colEndExclusive: number;
  chunkIndex: number;
  theadSource: HTMLTableSectionElement | null;
  sliceGroupId: string;
  withTopBorder: boolean;
  repeatLeadingCount: number;
  maxWidthPx: number;
  columnWidths: number[];
  hasContinuationRight: boolean;
  tbodyRowHeightsPx: Map<number, number>;
  tfootRowHeightsPx: Map<number, number>;
};

const buildWideTableChunkWrapper = (p: TBuildWideTableChunkWrapperParams): HTMLDivElement => {
  const copy = p.sourceTable.cloneNode(true) as HTMLTableElement;
  copy.style.removeProperty('width');
  copy.style.removeProperty('min-width');
  stripVirtualizedHeightsForPdf(copy);
  normalizeVirtualizedOffsetsForPdf(copy);
  normalizeBodyCellPositionForPdf(copy);
  normalizeLogicalColspanForPdfClone(copy);

  const copyThead = copy.querySelector('thead');
  const leadPrefixCols = p.repeatLeadingCount > 0 && p.colStart > 0 ? Math.min(p.repeatLeadingCount, p.colStart) : 0;
  const widthCtx: TPdfChunkColumnWidthContext = {
    columnWidths: p.columnWidths,
    colStart: p.colStart,
    colEndExclusive: p.colEndExclusive,
    leadPrefixCols,
  };

  const useSplitHead = Boolean(copyThead && p.theadSource && canTheadBeDetachedToSiblingTable(copy));
  let detachedThead: HTMLTableSectionElement | null = null;

  if (useSplitHead && copyThead && p.theadSource) {
    if (leadPrefixCols > 0) {
      detachedThead = buildMergedTheadWithLeadingColumns(
        p.theadSource,
        0,
        leadPrefixCols,
        p.colStart,
        p.colEndExclusive,
      );
    } else {
      detachedThead = buildSlicedTheadFromSource(p.theadSource, p.colStart, p.colEndExclusive);
    }
    copyThead.remove();
  } else if (p.theadSource && copyThead) {
    replaceTheadWithSliceDuplicate(copyThead, p.colStart, p.colEndExclusive, p.theadSource);
  }

  const tbodyLeadingClones = leadPrefixCols > 0 ? extractAllTbodyLeadingClonesForPdfChunk(copy, leadPrefixCols) : [];
  const tfootLeadingClones = leadPrefixCols > 0 ? extractAllTfootLeadingClonesForPdfChunk(copy, leadPrefixCols) : [];

  const theadAlreadySliced = useSplitHead || Boolean(p.theadSource && copy.querySelector('thead'));
  sliceTableToColumnRange(copy, p.colStart, p.colEndExclusive, { skipThead: theadAlreadySliced });

  if (tbodyLeadingClones.length) {
    prependLeadingClonesToTbodyRows(copy, tbodyLeadingClones);
  }
  if (tfootLeadingClones.length) {
    prependLeadingClonesToTfootRows(copy, tfootLeadingClones);
  }

  applyPdfChunkColumnWidths(copy, widthCtx);
  copy.style.maxWidth = `${p.maxWidthPx}px`;

  const wrapper = document.createElement('div');
  wrapper.className =
    p.chunkIndex > 0
      ? 'pdf-wide-table-slice-chunk pdf-wide-table-slice-chunk--continuation'
      : 'pdf-wide-table-slice-chunk';
  wrapper.setAttribute(PDF_EXPORT_SLICE_GROUP_ATTR, p.sliceGroupId);
  const chunkTotalWidthPx = getPdfChunkTableTotalWidthPx(widthCtx);

  if (!detachedThead) {
    applyTableTopBorderForPdfSlice(copy, p.sourceTable, p.withTopBorder);
    wrapper.appendChild(copy);
  } else {
    appendSplitHeadSliceToWrapper(wrapper, copy, detachedThead, p.sourceTable, p.withTopBorder);
    const headTable = wrapper.querySelector<HTMLTableElement>('.pdf-wide-table-slice-head-wrap table');
    if (headTable) {
      applyPdfChunkColumnWidths(headTable, widthCtx);
    }
    syncPdfSliceHeadBlockWidthToBody(wrapper, chunkTotalWidthPx);
  }

  if (p.hasContinuationRight) {
    wrapper.classList.add('pdf-wide-table-slice-chunk--has-right-continuation');
    if (isTableAggregatedLineMode(p.sourceTable)) {
      wrapper.classList.add('pdf-wide-table-slice-chunk--line-table');
    } else {
      markPdfChunkHorizontalCutEdgeCells(wrapper, p.sourceTable);
    }
  }

  // Html2pdf/html2canvas на границах страниц иногда подрезает последнюю строку,
  // если высоты строк на клонах плавают из-за изменения ширин/обрезки колонок.
  // Фиксируем высоту tr и высоты ячеек на tbody/tfoot по заранее замеренным значениям.
  applySectionRowExactHeightsPx(copy, 'tbody', p.tbodyRowHeightsPx);
  applySectionRowExactHeightsPx(copy, 'tfoot', p.tfootRowHeightsPx);

  return wrapper;
};

let pdfWideTableSliceGroupSeq = 0;

const replaceOriginalTableWithWidePdfChunks = (
  table: HTMLTableElement,
  plan: TWideTableSplitPlan,
  root: HTMLElement,
  withTopBorder: boolean,
  maxWidthPx: number,
): void => {
  const { chunks, columnWidths } = plan;
  const parent = table.parentElement;
  if (!parent) {
    return;
  }

  ensurePdfWideTableSliceStyles(root);

  const theadSource = table.querySelector('thead');
  stripVirtualizedHeightsForPdf(table);
  const tbodyRowHeightsPx = measureSectionRowHeightsPx(table, 'tbody');
  const tfootRowHeightsPx = measureSectionRowHeightsPx(table, 'tfoot');
  const repeatLeadingCount = parsePdfRepeatLeadingColumnCount(table);
  const sliceGroupId = `pdf-slice-${(pdfWideTableSliceGroupSeq += 1)}`;

  const fragment = document.createDocumentFragment();
  const chunkWrappers: HTMLDivElement[] = [];

  chunks.forEach(([c0, c1], index) => {
    const wrapper = buildWideTableChunkWrapper({
      sourceTable: table,
      colStart: c0,
      colEndExclusive: c1,
      chunkIndex: index,
      theadSource,
      sliceGroupId,
      withTopBorder,
      repeatLeadingCount,
      maxWidthPx,
      columnWidths,
      hasContinuationRight: index < chunks.length - 1,
      tbodyRowHeightsPx,
      tfootRowHeightsPx,
    });
    chunkWrappers.push(wrapper);
    fragment.appendChild(wrapper);
  });

  parent.insertBefore(fragment, table);
  table.remove();
};

/**
 * Разбивает широкие таблицы в уже отклонированном корне: вставляет копии с подмножеством колонок
 * и разрывы страниц между ними. Исходный узел `root` кратковременно монтируется в document для измерений.
 */
export const splitWideTablesForPdfClone = (root: HTMLElement, options: TSplitWideTablesForPdfCloneOptions): void => {
  const tableSelector = options.tableSelector ?? 'table';
  const layoutWidthPx = options.layoutWidthPx ?? PDF_EXPORT_WIDE_TABLE_LAYOUT_WIDTH_PX;
  const maxWidthPx = options.maxWidthPx;
  const withTopBorder = options.withTopBorder !== false;

  const host = mountCloneForMeasure(root, layoutWidthPx);

  try {
    const matched = Array.from(root.querySelectorAll<HTMLTableElement>(tableSelector));
    const tables = filterTopLevelTables(matched);

    for (const table of tables) {
      if (isWideTablePdfSplitDisabled(table)) {
        prepareNoWideSplitTableForPdfExport(table);
        continue;
      }

      const releaseIntrinsic = withIntrinsicTableWidth(table);

      try {
        const plan = tryComputeWideTableSplitPlan(table, maxWidthPx);
        if (!plan) {
          continue;
        }

        replaceOriginalTableWithWidePdfChunks(table, plan, root, withTopBorder, maxWidthPx);
      } finally {
        releaseIntrinsic();
      }
    }

    resetTableAggregatedFixedColumnOffsetsForPdfClone(root);
  } finally {
    root.remove();
    host.remove();
  }
};

/** Готовый `clonedNodeFormatter` для `AggregatedPageWrapper` + `downloadPDF` (нарезка широких таблиц). */
export const buildAggregatedPagePdfTablesCloneFormatter = (
  overrides?: Partial<
    Pick<TSplitWideTablesForPdfCloneOptions, 'maxWidthPx' | 'tableSelector' | 'layoutWidthPx' | 'withTopBorder'>
  >,
): ((clonedRoot: HTMLElement) => void) => {
  return (clonedRoot: HTMLElement) => {
    const layoutWidthPx = overrides?.layoutWidthPx ?? PDF_EXPORT_WIDE_TABLE_LAYOUT_WIDTH_PX;
    splitWideTablesForPdfClone(clonedRoot, {
      maxWidthPx: overrides?.maxWidthPx ?? getPdfWideTableChunkMaxWidthPx(layoutWidthPx),
      tableSelector: overrides?.tableSelector ?? '.aggregated-page__table table',
      layoutWidthPx,
      withTopBorder: overrides?.withTopBorder,
    });
  };
};
